import axios from "axios";
import { api } from "./_client";

export async function listProjectFiles(projectId, kind = null) {
    const params = kind ? `?kind=${encodeURIComponent(kind)}` : "";
    const { data } = await api.get(`/projects/${projectId}/files${params}`);
    return data;
}

export async function uploadProjectFile(projectId, file, onProgress, kind = "file") {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post(
        `/projects/${projectId}/files?kind=${encodeURIComponent(kind)}`,
        form,
        {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (e) => {
                if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
            },
        },
    );
    return data;
}

// iter-100 — manual expense entry (no file). Mirrors uploadProjectFile
// shape so the panel can refresh its list the same way.

export async function addManualExpense(projectId, payload, kind = "receipt") {
    const { data } = await api.post(
        `/projects/${projectId}/files/manual?kind=${encodeURIComponent(kind)}`,
        payload,
    );
    return data;
}

export async function deleteProjectFile(projectId, fileId) {
    const { data } = await api.delete(`/projects/${projectId}/files/${fileId}`);
    return data;
}

// Fetch the file via axios (which carries our Authorization header) and
// return a Blob URL so the caller can do an authenticated open/download
// without a query-string token round-trip.

export async function fetchProjectFileBlobUrl(projectId, fileId) {
    const res = await api.get(`/projects/${projectId}/files/${fileId}/download`, {
        responseType: "blob",
    });
    return URL.createObjectURL(res.data);
}

export async function listUploads(projectId) {
    const { data } = await api.get("/uploads", {
        params: projectId ? { project_id: projectId } : {},
    });
    return data;
}

export async function getUploadCounts() {
    const { data } = await api.get("/uploads/counts");
    return data; // { [project_id]: count }
}

// Chunked resumable upload (tus-lite). The flow:
//  1. POST /uploads/session  → {id, bytes_received: 0}
//  2. For each 2 MB chunk: PUT /uploads/session/{id}/chunk?offset=N with retry
//  3. POST /uploads/session/{id}/complete → returns the uploaded file record
//
// Resume across Wi-Fi drops is automatic: each chunk retries up to 4 times
// with exponential backoff. Resume across tab reloads is opt-in via the
// localStorage key `planflow.upload.resume.{projectId}.{fileKey}` — when
// present, we query the session for `bytes_received` and skip those bytes
// from the File object before starting chunk PUTs. Chunks that were partially
// received on the server are truncated server-side on error, so re-sending
// the same chunk at the same offset always works.
const CHUNK_SIZE = 2 * 1024 * 1024; // 2 MB — balanced for throughput vs retry cost
const SMALL_FILE_LIMIT = 4 * 1024 * 1024; // 4 MB: keep classic single-shot path
const MAX_CHUNK_RETRIES = 4;
const RESUME_KEY_PREFIX = "planflow.upload.resume.";

function _fileKey(file) {
    // Conservative de-dup for localStorage resume lookup — two different
    // files with the same (name, size, lastModified) can still collide, but
    // then they're semantically the same file from the user's POV anyway.
    return `${file.name}|${file.size}|${file.lastModified || 0}`;
}

function _loadResumeId(projectId, file) {
    try {
        const v = localStorage.getItem(
            `${RESUME_KEY_PREFIX}${projectId || "none"}.${_fileKey(file)}`,
        );
        return v || null;
    } catch {
        return null;
    }
}

function _saveResumeId(projectId, file, sessionId) {
    try {
        localStorage.setItem(
            `${RESUME_KEY_PREFIX}${projectId || "none"}.${_fileKey(file)}`,
            sessionId,
        );
    } catch {
        /* quota or private mode */
    }
}

function _clearResumeId(projectId, file) {
    try {
        localStorage.removeItem(
            `${RESUME_KEY_PREFIX}${projectId || "none"}.${_fileKey(file)}`,
        );
    } catch {
        /* noop */
    }
}

async function _putChunk(sessionId, offset, blob) {
    // Axios sends a File/Blob as the raw body when we pass it directly.
    return api.put(`/uploads/session/${sessionId}/chunk`, blob, {
        params: { offset },
        headers: {
            // Important: override default Content-Type so Axios/browser
            // doesn't try to do multipart encoding here.
            "Content-Type": blob.type || "application/octet-stream",
        },
        // Chunks are small — give each a short-ish timeout so a stuck
        // connection surfaces fast and we move on to the retry path.
        timeout: 60000,
    });
}

async function _uploadChunked(file, projectId, onProgress) {
    // 1. Try to resume an existing session.
    let sessionId = _loadResumeId(projectId, file);
    let bytesReceived = 0;
    if (sessionId) {
        try {
            const { data } = await api.get(`/uploads/session/${sessionId}`);
            if (
                data.status === "in_progress" &&
                data.total_size === file.size
            ) {
                bytesReceived = data.bytes_received;
            } else {
                sessionId = null; // stale / different file — start fresh
            }
        } catch {
            sessionId = null;
        }
    }

    // 2. Create a new session if there isn't one to resume.
    if (!sessionId) {
        const { data } = await api.post("/uploads/session", {
            filename: file.name,
            total_size: file.size,
            content_type: file.type || "application/octet-stream",
            project_id: projectId || null,
        });
        sessionId = data.id;
        bytesReceived = 0;
        _saveResumeId(projectId, file, sessionId);
    }

    if (onProgress && file.size > 0) {
        onProgress(Math.min(99, Math.round((bytesReceived / file.size) * 100)));
    }

    // 3. Loop over remaining chunks. On network error retry with backoff.
    while (bytesReceived < file.size) {
        const end = Math.min(bytesReceived + CHUNK_SIZE, file.size);
        const blob = file.slice(bytesReceived, end);
        let attempt = 0;
        let putRes = null;
        while (attempt < MAX_CHUNK_RETRIES) {
            try {
                putRes = await _putChunk(sessionId, bytesReceived, blob);
                break;
            } catch (err) {
                attempt += 1;
                const status = err?.response?.status;
                // Offset mismatch (409) — re-sync from the server and retry
                // the correct next chunk (e.g. after a partial network error
                // the server may have truncated back to bytesReceived, in
                // which case this resolves on the next iteration).
                if (status === 409) {
                    try {
                        const { data } = await api.get(
                            `/uploads/session/${sessionId}`,
                        );
                        bytesReceived = data.bytes_received;
                    } catch {
                        /* keep retrying */
                    }
                    break; // break inner retry loop; outer while re-slices at new offset
                }
                // 4xx (other than 409) — don't retry, surface the error.
                if (status && status >= 400 && status < 500 && status !== 408) {
                    throw err;
                }
                if (attempt >= MAX_CHUNK_RETRIES) throw err;
                await new Promise((r) => setTimeout(r, 600 * 2 ** attempt));
            }
        }
        if (putRes) {
            bytesReceived = putRes.data.bytes_received;
            if (onProgress && file.size > 0) {
                onProgress(
                    Math.min(99, Math.round((bytesReceived / file.size) * 100)),
                );
            }
        }
    }

    // 4. Finalize — server runs pypdf split + scale detect on the assembled
    // file. This can take a few seconds on a 1000-page PDF so give it a
    // generous timeout.
    const { data: record } = await api.post(
        `/uploads/session/${sessionId}/complete`,
        {},
        { timeout: 5 * 60 * 1000 },
    );
    _clearResumeId(projectId, file);
    if (onProgress) onProgress(100);
    return record;
}

export async function uploadPlan(file, projectId, onProgress) {
    // Small files skip the session dance — lower overhead for e.g. a 200 KB
    // image upload. Anything above SMALL_FILE_LIMIT goes through the
    // resumable chunked path so a Wi-Fi drop mid-upload is recoverable.
    if (file.size <= SMALL_FILE_LIMIT) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd, {
            params: projectId ? { project_id: projectId } : {},
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (e) => {
                if (!onProgress) return;
                const pct = e.total
                    ? Math.min(100, Math.round((e.loaded / e.total) * 100))
                    : 0;
                onProgress(pct);
            },
        });
        return data;
    }
    return _uploadChunked(file, projectId, onProgress);
}

export async function deleteUpload(id) {
    const { data } = await api.delete(`/uploads/${id}`);
    return data;
}

// iter-53: one-shot AI call that finds dimension annotations on page 1
// of an uploaded plan and derives the drawing scale from the largest
// horizontal + vertical callouts. Saves the result onto the upload and
// returns the updated row + detection summary.

export async function autoDetectUploadScale(uploadId) {
    const { data } = await api.post(
        `/takeoff/upload/${uploadId}/auto-scale`,
    );
    return data;
}

// Trigger a browser "Save As" for a single uploaded plan file.
// Streams the bytes through /api/files/{storage_path} using the current
// auth token, then wraps them in a Blob and clicks a temporary <a>.

export async function downloadUploadFile(upload) {
    if (!upload?.storage_path) throw new Error("Missing storage_path");
    const res = await api.get(`/files/${upload.storage_path}`, {
        responseType: "blob",
    });
    const mime = upload.content_type || res.data?.type || "application/octet-stream";
    const blob = new Blob([res.data], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = upload.original_filename || "plan";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Bundle every uploaded plan for a project into a single ZIP and trigger
// a browser "Save As". Uses client-side JSZip so we don't add any new
// backend surface area.

export async function downloadAllUploads(uploads, zipName = "project_plans") {
    const list = (uploads || []).filter((u) => u && u.storage_path);
    if (!list.length) return 0;
    const { default: JSZip } = await import("jszip");
    const zip = new JSZip();
    // Pull every file in parallel — keeps the wall-clock time bounded by
    // the slowest request rather than the sum of them.
    const fetched = await Promise.all(
        list.map(async (u) => {
            const res = await api.get(`/files/${u.storage_path}`, {
                responseType: "blob",
            });
            return { upload: u, blob: res.data };
        }),
    );
    // Dedupe duplicate filenames by suffixing " (2)", " (3)"...
    const seen = new Map();
    for (const { upload, blob } of fetched) {
        let fname = upload.original_filename || `plan-${upload.id}`;
        const count = (seen.get(fname) || 0) + 1;
        seen.set(fname, count);
        if (count > 1) {
            const dot = fname.lastIndexOf(".");
            fname = dot > 0
                ? `${fname.slice(0, dot)} (${count})${fname.slice(dot)}`
                : `${fname} (${count})`;
        }
        zip.file(fname, blob);
    }
    const zipBlob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(zipName || "plans").replace(/\s+/g, "_")}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return list.length;
}

export async function splitUpload(id) {
    const { data } = await api.post(`/uploads/${id}/split`, {}, { timeout: 120000 });
    return data;
}

export async function pinUploadPage(uploadId, pageNumber) {
    const { data } = await api.post(
        `/uploads/${uploadId}/pages/${pageNumber}/pin`,
        {},
        { timeout: 120000 },
    );
    return data;
}

export async function getUpload(id) {
    const { data } = await api.get(`/uploads/${id}`);
    return data;
}

export async function patchUpload(id, patch) {
    const { data } = await api.patch(`/uploads/${id}`, patch);
    return data;
}

export async function reorderUploads(ids) {
    const { data } = await api.post(`/uploads/reorder`, { order: ids });
    return data;
}

export async function downloadPinnedPdf(projectId, projectName = "project") {
    const res = await api.get(`/projects/${projectId}/pinned-pdf`, {
        responseType: "blob",
    });
    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(projectName || "project").replace(/\s+/g, "_")}_pinned.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ----- Contacts CRUD -----
